import { useEffect, useLayoutEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'

// pages & components
import Home from './pages/Home'
import FAQ from './pages/FAQ'
import AllWebsites from './pages/AllWebsites'
import Reviews from './pages/Reviews'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import CasesPage from './pages/CasesPage'
import PopupGate from './components/PopupGate'
import TermsOfService from './pages/TermsOfService'
import NotAvailable from './pages/NotAvailable'
import BestCasesPage from "./pages/BestCasesPage";
import Giveaways from './pages/Giveaways'
import './App.css'
import CookieBanner from './components/CookieBanner'
import { trackPageView } from './utils/tracking'

function AppLayout() {
  const location = useLocation();
  const hideChrome = location.pathname === '/not-available';
  const previousPage = useRef(null)

  useLayoutEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }

    resetScroll()
    const animationFrame = window.requestAnimationFrame(resetScroll)

    return () => window.cancelAnimationFrame(animationFrame)
  }, [location.key])

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined

    const previousScrollRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = "manual"

    return () => {
      window.history.scrollRestoration = previousScrollRestoration
    }
  }, [])

  useEffect(() => {
    const currentPage = `${location.pathname}${location.search}`

    if (previousPage.current === null) {
      previousPage.current = currentPage
      return
    }

    if (previousPage.current !== currentPage) {
      previousPage.current = currentPage
      trackPageView()
    }
  }, [location.pathname, location.search])

  return (
      <PopupGate>
        <div className="min-h-screen flex flex-col text-white">
          {/* Main background with gradient */}
          <div className="fixed inset-0 bg-gradient-to-b from-[#0A0C10] via-[#0F1215] to-[#0A0C10] -z-10"></div>

          {/* Grid pattern */}
          <div
            className="fixed inset-0 bg-center bg-no-repeat opacity-[0.02] pointer-events-none -z-10"
            style={{ backgroundImage: "url('/grid.svg')" }}
          ></div>

          {/* Decorative glowing orbs */}
          <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] bg-[#FF6B00] rounded-full opacity-5 blur-[100px] pointer-events-none -z-10"></div>
          <div className="fixed bottom-1/3 right-1/4 w-[400px] h-[400px] bg-[#FF9A3D] rounded-full opacity-5 blur-[100px] pointer-events-none -z-10"></div>

          {/* Decorative circles */}
          <div className="fixed top-20 left-[15%] w-32 h-32 border border-[#FF6B00]/20 rounded-full animate-pulse-slow -z-10"></div>
          <div className="fixed bottom-40 right-[15%] w-24 h-24 border border-[#FF6B00]/20 rounded-full animate-pulse-slow delay-700 -z-10"></div>
          <div className="fixed top-[40%] right-[25%] w-16 h-16 border border-[#FF6B00]/10 rounded-full animate-pulse-slow delay-300 -z-10"></div>

          {!hideChrome && <Navbar />}
          <main className="flex-grow relative z-10">
            <Routes>
              <Route path='/' element={<Home />} />
              <Route path='/websites' element={<AllWebsites />} />
              <Route path='/websites/:site' element={<CasesPage />} />
              <Route path='/bonuses' element={<Reviews />} />
              <Route path='/faq' element={<FAQ />} />
              <Route path='/terms-of-service' element={<TermsOfService />} />
              <Route path='/not-available' element={<NotAvailable />} />
              <Route path='/giveaways' element={<Giveaways />} />
              <Route path="/best-cases" element={<BestCasesPage />} />
            </Routes>
          </main>
          {!hideChrome && <Footer />}
          <Analytics />
          {!hideChrome && <CookieBanner />}
        </div>
      </PopupGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  )
}
